import type { ReactElement } from 'react';
import { Bot } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type {
  AuditActorKeyRef,
  AuditApiKeyRef,
} from '@kirin/api-client/contracts/audit-logs';

/**
 * Teal so the badge reads as a different *kind* of fact from the operation
 * badges beside it (which own purple/amber/red/green/orange/blue). Same
 * border/text opacity pair as `OperationBadge`, so it carries the same weight
 * rather than shouting.
 */
const API_KEY_BADGE_COLOR =
  'border-teal-200/60 text-teal-600/80 bg-transparent dark:border-teal-800/40 dark:text-teal-400/70';

function KeySourceBadge({
  label,
  tooltip,
}: {
  label: string;
  tooltip: string;
}): ReactElement {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={`${API_KEY_BADGE_COLOR} shrink-0 cursor-help gap-1 px-1.5 py-0 text-[10px] font-normal`}
          >
            <Bot className="size-3 shrink-0" aria-hidden="true" />
            <span className="truncate">{label}</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Marks an audit row that a management API key produced (KIR-3751).
 *
 * The row's user column still names the person who minted the key — they remain
 * accountable — so this badge answers "by what mechanism", not "by whom". Without
 * it a SIEM integration's 3am write is indistinguishable from that person
 * working at 3am.
 *
 * An unnamed key renders a bare `API` rather than `API:` with nothing after it:
 * the write side stores `apiKeyName` as an empty string when a key was minted
 * without a label, so "has an id" and "has a name" are separate questions.
 */
export function ApiKeySourceBadge({
  apiKey,
}: {
  apiKey: AuditApiKeyRef;
}): ReactElement {
  const label = apiKey.name === null ? 'API' : `API: ${apiKey.name}`;
  // The id is the Descope access-key id, which is what Settings → API Keys
  // shows as the key's identity — so a SOC engineer can match row to key.
  return (
    <KeySourceBadge
      label={label}
      tooltip={`Management API key: ${apiKey.id}`}
    />
  );
}

const ORG_KEY_LABELS: Record<AuditActorKeyRef['kind'], string> = {
  mdm: 'MDM key',
  plugin: 'Plugin key',
  device: 'Device key',
};

/** Marks a user-less row written with an org-level MDM, plugin or device key. */
export function OrgKeySourceBadge({
  actorKey,
}: {
  actorKey: AuditActorKeyRef;
}): ReactElement {
  const label = ORG_KEY_LABELS[actorKey.kind];
  return <KeySourceBadge label={label} tooltip={`${label}: ${actorKey.id}`} />;
}
